// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/asset.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / header-actions).

import type { ListPageConfig } from "../../components/composition/ListPage";
import { assetsApi } from "../../api/assets";
import type { Asset } from "../../api/types";
// override-block:imports BEGIN
import { createElement, type ReactNode } from "react";
import type { AssetKind, AssetSource } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// override-block:imports END

// override-block:helpers BEGIN
const KIND_LABEL: Record<AssetKind, string> = {
  photo: "Photo",
  logo: "Logo",
  illustration: "Illustration",
  pattern: "Pattern",
  video: "Video",
};
const SOURCE_LABEL: Record<AssetSource, string> = {
  uploaded: "Uploaded",
  ai_generated: "AI-generated",
  platform_authentic: "Platform-curated",
};

function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function thumbnailUrl(a: Asset): string | null {
  return a.rendered_sizes?.thumbnail ?? a.thumbnail_url ?? a.primary_url ?? null;
}

function dimensionsLabel(a: Asset): string {
  if (a.kind === "video") {
    return a.duration_seconds != null ? `${a.duration_seconds}s` : "—";
  }
  if (a.width != null && a.height != null) return `${a.width}×${a.height}`;
  return "—";
}

// Column render helpers — the generator emits `render: <fieldCamel>Column`
// for any column whose `type` is custom / enum / unknown-to-contract.
const thumbnailColumn = (row: Asset): ReactNode => {
  const url = thumbnailUrl(row);
  return url
    ? createElement("img", {
        src: url,
        alt: row.alt_text ?? row.title ?? "",
        loading: "lazy",
        style: {
          width: 40,
          height: 40,
          objectFit: "cover",
          borderRadius: 6,
          background: "var(--color-surface-2)",
        },
      })
    : createElement(
        "div",
        {
          style: {
            width: 40,
            height: 40,
            borderRadius: 6,
            background: "var(--color-surface-2)",
          },
        },
        null,
      );
};
const kindColumn = (row: Asset): ReactNode => KIND_LABEL[row.kind] ?? row.kind;
const dimensionsColumn = (row: Asset): ReactNode => dimensionsLabel(row);
const byteSizeColumn = (row: Asset): ReactNode => formatBytes(row.byte_size);
const sourceColumn = (row: Asset): ReactNode => SOURCE_LABEL[row.source] ?? row.source;
// override-block:helpers END

export const assetListConfig: ListPageConfig<Asset> = {
  title: "Assets",
  subtitle: "Org-uploaded media + platform-curated stock — the library every page, message, and campaign draws from.",
  queryKey: ["assets", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search }) =>
    assetsApi.listAssets({ page, pageSize, sortField, sortDirection, search }),
  defaultSort: { field: "created_at", direction: "desc" },
  pageSize: 50,
  rowHref: "/assets/{id}",
  search: { enabled: true, placeholder: "Search assets by title, alt text, or tag…" },
  emptyState: {
    title: "No assets yet",
    subtitle: "Upload an image, logo, or video to get started — anything you upload here is available to every form that picks an asset.",
  },
  headerActions: [
    // override-block:header-actions BEGIN
    { id: "upload", label: "Upload", variant: "primary", permissionGate: toGate(GATE_CATALOG.asset_list["upload"], "hide") },
    { id: "generate-ai", label: "Generate with AI", variant: "secondary", permissionGate: toGate(GATE_CATALOG.asset_list["generate-ai"], "hide") },
    // override-block:header-actions END
  ],
  bulkActions: [
    {
      id: "bulk-delete",
      label: "Delete",
      permission: "alora-giving.asset.delete",
      onAction:
        // override-block:bulk-action-bulk-delete BEGIN
        () => Promise.resolve(),
        // override-block:bulk-action-bulk-delete END
    },
    {
      id: "bulk-tag",
      label: "Tag",
      permission: "alora-giving.asset.update",
      onAction:
        // override-block:bulk-action-bulk-tag BEGIN
        () => Promise.resolve(),
        // override-block:bulk-action-bulk-tag END
    },
  ],
  columns: [
    { field: "thumbnail", label: "", type: "custom", width: "56px", render: thumbnailColumn },
    { field: "title", label: "Name", type: "text", sortable: true },
    { field: "kind", label: "Type", type: "custom", render: kindColumn },
    { field: "dimensions", label: "Dimensions", type: "custom", render: dimensionsColumn },
    { field: "byte_size", label: "Size", type: "custom", sortable: true, render: byteSizeColumn },
    { field: "used_in_count", label: "Used in", type: "number", sortable: true },
    { field: "created_at", label: "Uploaded", type: "relative-date", sortable: true },
    { field: "source", label: "Source", type: "custom", render: sourceColumn },
  ],
};
